import React, { useEffect, useState } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highmaps";
import dataModule from "highcharts/modules/data";
import drilldown from "highcharts/modules/drilldown";
import { FormattedMessage } from "react-intl";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import User from "@mapmycustomers/shared/types/User";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import { LoadingSpinner } from "@mapmycustomers/ui";

import TextWithLine from "@app/component/typography/TextWithLine";
import styles from "@app/scene/reports/components/Deals/DealsCard.module.scss";
import DealGeographyMapFilter from "@app/scene/reports/components/MapFilters/DealGeographyMapFilter";
import MapFilter from "@app/scene/reports/enums/MapFilter";
import {
  getFeaturePathByMapFilter,
  reportsDealsAnalytics,
} from "@app/scene/reports/reportsAnalytics";
import { fetchDealsGeoChartData } from "@app/scene/reports/store/actions";
import { getDealsGeoChartData } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";

import performAutoDrilldown from "./performAutoDrilldown";
import useGeoChartOptions from "./useGeoChartOptions";

interface Props {
  funnelId: Funnel["id"] | undefined;
  userIds: Array<User["id"]>;
}

dataModule(Highcharts);
drilldown(Highcharts);

const DealsByGeographyChart: React.FC<
  Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps
> = ({ dealsCountByHcKey, fetchDeals, funnelId, loading, userIds }) => {
  const [filter, setFilter] = useState<MapFilter>(MapFilter.CLOSED_WON_REVENUE);
  useChangeTracking(
    () =>
      reportsDealsAnalytics.clicked(["Geography Reporting", ...getFeaturePathByMapFilter(filter)]),
    [filter]
  );

  useEffect(() => {
    if (funnelId) {
      fetchDeals({ filter, funnelId, userIds });
    }
  }, [fetchDeals, funnelId, userIds, filter]);

  const mapOptions = useGeoChartOptions(dealsCountByHcKey);

  if (!mapOptions) {
    return <LoadingSpinner />;
  }

  return (
    <Row className={styles.dealsByGeographySection}>
      <Col span={16}>
        <TextWithLine>
          <FormattedMessage
            id="reports.deals.geography"
            defaultMessage="Reporting by Geography"
            description="Title for Reporting by Geography section on Deals Screen"
          />
        </TextWithLine>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <HighchartsReact
            callback={performAutoDrilldown(dealsCountByHcKey)}
            constructorType={"mapChart"}
            highcharts={Highcharts}
            immutable
            options={mapOptions}
          />
        )}
      </Col>
      <Col className={styles.mapFiltersContainer} span={6}>
        <DealGeographyMapFilter filter={filter} onChange={setFilter} />
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  dealsCountByHcKey: getDealsGeoChartData(state).dealsCountByHcKey,
  loading: getDealsGeoChartData(state).loading,
});

const mapDispatchToProps = {
  fetchDeals: fetchDealsGeoChartData.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealsByGeographyChart);
